import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { loginUser, logoutUser, registerUser, getCurrentUserDetails } from '../apis/APICalls';
import { useApiCall } from '../hooks/useApiCall';

// Create Context
const AuthContext = createContext();

// Define initial state
const initialAuthState = {
    isAuthenticated: false,
    user: null, // Will store user details like username, email, pk
    isLoading: true,
    error: null,
};

// Create Provider Component
export const AuthProvider = ({ children }) => {
    const [authState, setAuthState] = useState(initialAuthState);
    const { executeApiCall } = useApiCall();

    // Check authentication status on initial load
    const checkAuthStatus = useCallback(async () => {
        console.log("checkAuthStatus called. Making request to /api/auth/user/");
        setAuthState(prev => ({ ...prev, isLoading: true }));
        try { 
            await executeApiCall(() => getCurrentUserDetails(), {
                onSuccess: (userData) => {
                    console.log("Successfully fetched user details:", userData);
                    if (userData && userData.pk) {
                        setAuthState({
                            isAuthenticated: true,
                            user: userData,
                            isLoading: false,
                            error: null,
                        });
                    } else {
                        setAuthState({
                            isAuthenticated: false,
                            user: null,
                            isLoading: false,
                            error: null,
                        });
                    }
                },
                onError: (err) => {
                    console.log("onError in checkAuthStatus (User likely not logged in):", err);
                    setAuthState({
                        isAuthenticated: false,
                        user: null,
                        isLoading: false,
                        error: null,
                    });
                },
                resetDataOnCall: false, 
                storeResponse: false, 
            });
        } catch (error) {
            console.log("Caught error in checkAuthStatus after executeApiCall:", error);
            if (error.status !== 401 && error.status !== 403) { 
                 console.error("Unexpected error during checkAuthStatus:", error);
            }
            setAuthState(prev => ({ ...prev, isLoading: false }));
        }
    }, [executeApiCall]);

    useEffect(() => {
        checkAuthStatus();
    }, [checkAuthStatus]);

    // Email/password login
    const login = async (credentials) => {
        setAuthState(prev => ({ ...prev, isLoading: true, error: null }));
        try {
            const response = await loginUser(credentials);
            // Our custom login endpoint returns user data directly
            if (response && response.pk) {
                setAuthState(prev => ({
                    ...prev,
                    user: response,
                    isAuthenticated: true,
                    isLoading: false,
                    error: null
                }));
            } else {
                // Fallback: check auth status
                await checkAuthStatus();
            }
        } catch (err) {
            setAuthState(prev => ({ ...prev, error: err.response?.data?.detail || 'Login failed', isLoading: false }));
        }
    };

    // Registration
    const register = async (registrationData) => {
        setAuthState(prev => ({ ...prev, isLoading: true, error: null }));
        try {
            const response = await registerUser(registrationData);
            console.log('Registration response:', response);
            
            // Our custom registration endpoint returns user data directly
            if (response && response.pk) {
                console.log('Setting user from response:', response);
                setAuthState(prev => ({
                    ...prev,
                    user: response,
                    isAuthenticated: true,
                    isLoading: false,
                    error: null
                }));
            } else {
                console.log('No user data in response, checking auth status...');
                setTimeout(async () => {
                    await checkAuthStatus();
                }, 500);
            }
        } catch (err) {
            console.error('Registration error:', err);
            setAuthState(prev => ({ ...prev, error: err.response?.data?.detail || 'Registration failed', isLoading: false }));
        }
    };

    // Google login
    const googleLogin = async () => {
        setAuthState(prev => ({ ...prev, error: 'Google login not available in this version', isLoading: false }));
    };

    // Logout
    const logout = async () => {
        setAuthState(prev => ({ ...prev, isLoading: true, error: null }));
        try {
            await logoutUser();
            // After successful logout, check the authentication status
            await checkAuthStatus();
        } catch (err) {
            setAuthState(prev => ({ ...prev, error: 'Logout failed', isLoading: false }));
        }
    };

    return (
        <AuthContext.Provider value={{
            user: authState.user,
            isAuthenticated: authState.isAuthenticated,
            isLoading: authState.isLoading,
            error: authState.error,
            login,
            register,
            googleLogin,
            logout,
            setError: (msg) => setAuthState(prev => ({ ...prev, error: msg })),
        }}>
            {children}
        </AuthContext.Provider>
    );
};

// Custom hook to use the auth context
export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}; 